"use client";

import Button from "@/components/Button";
import RTHMLogo from "@/components/SVG/Logos/RTHMLogo";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import React from "react";

import Facebook from "../SVG/Facebook";
import Instagram from "../SVG/Instagram";
import RTHMIntelligenceLogo from "../SVG/Logos/RTHMIntelligence";
import RTHMIntelligenceLogoVertical from "../SVG/Logos/RTHMIntelligenceVertical";
import X from "../SVG/X";
import YouTube from "../SVG/YouTube";

export default function FooterClient() {
    const posthog = usePostHog();
    const pathname = usePathname();

    return (
        <footer className="bg-[#1a5b77] dark:bg-blue-900/30 w-full">
            <div className="pt-14 w-full">
                {/* Main Footer Content */}
                <div className="px-4 md:px-16 border-b border-white/20 pb-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {/* Logo and Description */}
                        <div className="flex flex-col">
                            {pathname.includes("/intelligence") ? (
                                <>
                                    <RTHMIntelligenceLogo
                                        className="md:hidden"
                                        fillColor="white"
                                    />
                                    <RTHMIntelligenceLogoVertical
                                        className="hidden md:flex"
                                        fillColor="white"
                                    />
                                </>
                            ) : (
                                <RTHMLogo
                                    link="/"
                                    className="shrink-0"
                                    fillColor="white"
                                />
                            )}
                            <p className="text-white/80 text-base font-light font-space leading-tight max-w-sm mt-4">
                                {pathname.includes("/intelligence")
                                    ? `RTHM Intelligence is a comprehensive health analytics and personalized medicine module designed to help users better understand their health conditions, symptoms, and treatment options.`
                                    : `RTHM Direct is an online treatment platform
                                providing access to research supported
                                treatments to ease the symptoms of Long COVID,
                                ME/CFS, and other complex illnesses.`}
                            </p>
                            {/* LegitScript Seal */}
                            <Link
                                href="https://www.legitscript.com/websites/?checker_keywords=rthm.com"
                                target="_blank"
                                className="block w-[100px] h-[85px] mt-4"
                                onClick={() => {
                                    posthog?.capture(
                                        "clicked_legitscript_seal",
                                    );
                                }}
                            >
                                <Image
                                    src="https://static.legitscript.com/seals/15482469.png"
                                    alt="LegitScript approved"
                                    width={100}
                                    height={85}
                                    loading="lazy"
                                />
                            </Link>
                        </div>

                        {/* Empty Middle Column for Layout */}
                        <div className="hidden lg:block" />

                        {/* Newsletter Signup */}
                        <div className="flex flex-col items-start md:items-end justify-between w-full md:col-span-2 lg:col-span-1">
                            <div className="flex flex-col gap-4 w-full">
                                <h3 className="text-white text-lg font-medium font-satoshi uppercase text-right">
                                    Join our mailing list
                                </h3>
                                <div id="mc_embed_signup" className="w-full">
                                    <form
                                        action="https://rthm.us14.list-manage.com/subscribe/post?u=48ca5f943afe3a05c70d3bd9a&amp;id=99411d3d07&amp;f_id=00d95ce0f0"
                                        method="post"
                                        id="mc-embedded-subscribe-form"
                                        name="mc-embedded-subscribe-form"
                                        target="_blank" // Added this line
                                        noValidate
                                        className="validate flex flex-row gap-1 w-full"
                                    >
                                        <div className="mc-field-group flex-1">
                                            <input
                                                aria-label="Email"
                                                type="email"
                                                name="EMAIL"
                                                placeholder="Email"
                                                className="required email text-white text-sm font-light bg-white/10 font-space rounded-md border border-white/40 placeholder-white/80 focus:outline-hidden focus:border-white p-4 w-full"
                                                id="mce-EMAIL"
                                                required
                                            />
                                            {/* <div className="Email opacity-70 text-white text-sm font-light font-['Space Grotesk'] leading-[18.20px]">Email</div> */}
                                        </div>
                                        <div
                                            id="mce-responses"
                                            className="clear text-white text-sm"
                                        >
                                            <div
                                                className="response"
                                                id="mce-error-response"
                                                style={{ display: "none" }}
                                            ></div>
                                            <div
                                                className="response"
                                                id="mce-success-response"
                                                style={{ display: "none" }}
                                            ></div>
                                        </div>
                                        {/* Hidden input for bot protection */}
                                        <div
                                            aria-hidden="true"
                                            style={{
                                                position: "absolute",
                                                left: "-5000px",
                                            }}
                                        >
                                            <input
                                                type="text"
                                                name="b_48ca5f943afe3a05c70d3bd9a_f61086c5ce"
                                                tabIndex={-1}
                                                value=""
                                                readOnly
                                            />
                                        </div>
                                        <div className="clear shrink-0">
                                            <Button
                                                id="mc-embedded-subscribe"
                                                actionType="submit"
                                                text="Sign up"
                                                height="h-14"
                                                type="filled"
                                                className="w-32 uppercase"
                                                onClick={() => {
                                                    posthog?.capture(
                                                        "clicked_newsletter_signup",
                                                    );
                                                }}
                                            />
                                        </div>
                                    </form>
                                </div>
                            </div>
                            {/* Social Icons */}
                            <div className="flex gap-2 mt-8">
                                {[
                                    "Instagram",
                                    "Twitter",
                                    "Facebook",
                                    "Youtube",
                                ].map((social) => (
                                    <Link
                                        key={social}
                                        href={
                                            social === "Instagram"
                                                ? "https://www.instagram.com/rthm_health/"
                                                : social === "Twitter"
                                                  ? "https://x.com/RTHM_Health"
                                                  : social === "Facebook"
                                                    ? "https://www.facebook.com/HealthRTHM"
                                                    : "https://www.youtube.com/@RTHM_Health"
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() => {
                                            posthog?.capture(
                                                `clicked_social_icon`,
                                                {
                                                    social,
                                                },
                                            );
                                        }}
                                        className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
                                        aria-label={`Visit RTHM on ${social}`}
                                    >
                                        <span className="sr-only">
                                            Visit RTHM on {social}
                                        </span>
                                        {social === "Instagram" && (
                                            <Instagram />
                                        )}
                                        {social === "Twitter" && <X />}
                                        {social === "Facebook" && <Facebook />}
                                        {social === "Youtube" && <YouTube />}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Legal Text */}
                <div className="py-8 text-center">
                    <p className="text-white/80 text-sm font-light font-space leading-relaxed max-w-4xl mx-auto mb-3">
                        The federal Physician Payments Sunshine Act requires
                        that detailed information about payment and other
                        payments of value worth over ten dollars ($10) from
                        manufacturers of drugs, medical devices, and biologics
                        to physicians and teaching hospitals be made available
                        to the public
                    </p>
                    <p className="text-white/80 text-sm font-light font-space leading-relaxed">
                        2261 Market Street #10535, San Francisco, CA 94114
                    </p>
                </div>

                {/* Bottom Bar */}
                <div className="bg-white/10">
                    <div className="container mx-auto px-4 py-2 md:py-0 md:h-10 flex flex-col md:flex-row items-center md:justify-between gap-2 md:gap-0">
                        {/* Terms and Privacy - Will appear on top for mobile */}
                        <div className="flex items-center gap-2.5 text-white/90 text-sm font-light font-space order-1 md:order-2">
                            <Link
                                href="/legal/terms-of-use"
                                className="hover:text-white transition-colors"
                                target="_blank"
                                onClick={() => {
                                    posthog?.capture(
                                        "clicked_terms_and_conditions",
                                    );
                                }}
                            >
                                Terms and Conditions
                            </Link>
                            <span>|</span>
                            <Link
                                href="/legal/privacy-policy"
                                className="hover:text-white transition-colors"
                                target="_blank"
                                onClick={() => {
                                    posthog?.capture("clicked_privacy_policy");
                                }}
                            >
                                Privacy Policy
                            </Link>
                        </div>

                        {/* Copyright and Links - Will appear below for mobile */}
                        <div className="text-white/90 text-sm font-light font-space order-2 md:order-1 flex flex-col md:flex-row items-center gap-2">
                            <div>Copyright © RTHM, Inc.</div>
                            <div className="hidden md:flex items-center gap-2">
                                <span>|</span>
                                <Link
                                    href="/resources"
                                    className="hover:text-white transition-colors"
                                    onClick={() => {
                                        posthog?.capture(
                                            "clicked_resources_footer",
                                        );
                                    }}
                                >
                                    Resources
                                </Link>
                                <span>|</span>
                                <Link
                                    href="/team"
                                    className="hover:text-white transition-colors"
                                    onClick={() => {
                                        posthog?.capture("clicked_team_footer");
                                    }}
                                >
                                    Team
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}
